"use client";

import { type Config, config } from "@repo/config";
import {
	AI_REPORT_CREDITS,
	CITATION_OUTLINE_CREDITS,
	CITATION_REWRITE_CREDITS,
	TRACKED_PROMPT_OVERAGE_CREDITS,
} from "@repo/payments/client";
import { formatUsd } from "@saas/payments/lib/pricing-v2";
import { cn } from "@ui/lib";
import { CoinsIcon } from "lucide-react";
import { useTranslations } from "next-intl";

const plans = config.payments.plans as Config["payments"]["plans"];

const EXAMPLES = [
	{ key: "aiReport", credits: AI_REPORT_CREDITS },
	{ key: "pageRewrite", credits: CITATION_REWRITE_CREDITS },
	{ key: "contentBrief", credits: CITATION_OUTLINE_CREDITS },
	{ key: "extraPrompt", credits: TRACKED_PROMPT_OVERAGE_CREDITS },
] as const;

/**
 * The compact credits line under the plan cards: a few things the monthly
 * credits buy, so credits read as useful rather than as a meter. Costs come
 * from the billing constants. Pay-as-you-go sits beside it on the marketing
 * page only; a customer in the app already has a plan.
 */
export function CreditExamples({
	showPayAsYouGo = true,
	className,
}: {
	showPayAsYouGo?: boolean;
	className?: string;
}) {
	const t = useTranslations();
	const payg = plans.paygCredits;
	const creditPrice = payg?.prices?.[0]?.amount ?? 1;
	const minimum = payg?.minPurchaseAmount ?? 15;

	return (
		<div
			className={cn(
				"flex flex-col gap-4 text-sm lg:flex-row lg:items-center lg:justify-between lg:gap-8",
				className,
			)}
		>
			<div className="flex flex-col gap-2">
				<p className="flex items-center gap-1.5 text-muted-foreground">
					<CoinsIcon
						className="size-4 shrink-0 text-primary"
						aria-hidden
					/>
					{t("pricing.v2.creditExamples.intro")}
				</p>
				<ul className="flex flex-wrap gap-2">
					{EXAMPLES.map((ex) => (
						<li
							key={ex.key}
							className="inline-flex items-center gap-1.5 rounded-full border bg-card px-3 py-1"
						>
							{t(`pricing.v2.creditExamples.items.${ex.key}`)}
							<span className="tabular-nums text-muted-foreground">
								{t("pricing.v2.creditExamples.credits", {
									count: ex.credits,
								})}
							</span>
						</li>
					))}
				</ul>
			</div>
			{showPayAsYouGo ? (
				<p className="text-muted-foreground lg:max-w-sm lg:shrink-0 lg:text-right">
					<span className="font-semibold text-foreground">
						{t("pricing.v2.creditExamples.paygLead")}
					</span>{" "}
					{t("pricing.v2.creditExamples.paygBody", {
						price: formatUsd(creditPrice),
						minimum: formatUsd(minimum),
					})}
				</p>
			) : null}
		</div>
	);
}
